import { Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConfig, SharedAppSettingsService } from '@luggagehero/shared/app-settings/data-access';
import { Config } from '@luggagehero/shared/environment';
import { FeatureResolver } from '@luggagehero/shared/feature-resolution';
import { BookableStorageLocation, Booking, IPrice, LegacyOrder, Review } from '@luggagehero/shared/interfaces';
import { SharedAnalyticsService } from '@luggagehero/shared/services/analytics';
import { SharedBookingService } from '@luggagehero/shared/services/bookings';
import { SharedDocumentService } from '@luggagehero/shared/services/document';
import { SharedGoogleMapsService } from '@luggagehero/shared/services/google-maps';
import { SharedLocationService } from '@luggagehero/shared/services/locations';
import { SharedNotificationService } from '@luggagehero/shared/services/notification';
import { SharedReviewsService } from '@luggagehero/shared/services/reviews';
import { SharedShopsService } from '@luggagehero/shared/services/shops';
import { SharedStorageService } from '@luggagehero/shared/services/storage';
import { SharedWindowService } from '@luggagehero/shared/services/window';
import { afterPaymentInstructionKey } from '@luggagehero/shared/util';
import { dropoffWizardEligibility, DropoffWizardOutcome } from '@luggagehero/shared-feature-bookings-wizard';
import { TranslateService } from '@ngx-translate/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { isObservable, map, Observable, of as observableOf, Subscription } from 'rxjs';

import { BaseComponent, BaseModalComponent, ModalOptions, ModalRef } from '../../../../core';
import { ModalService } from '../../../../core/services/index';
import { DateUtil } from '../../../../utils/date.util';
import {
  BookingActionBaseComponent,
  BookingActionInfo,
  BookingActionState,
  BookingChange,
  DropOffActionBaseComponent,
  ExplainStorageTimerActionBaseComponent,
  ExploreExperiencesActionBaseComponent,
  GoToShopActionBaseComponent,
  LeaveFeedbackActionBaseComponent,
  ModifyBookingActionBaseComponent,
  ReportIncidentActionBaseComponent,
  SelectPaymentMethodActionBaseComponent,
  ShowReceiptActionBaseComponent,
  UndoDropOffActionBaseComponent,
  ValidateBookingActionBaseComponent,
} from './booking-actions';
import { BookingOptionsBaseComponent } from './booking-options.base-component';
import { BookingTimelineStrategy } from './booking-strategies';
import { BookingTimelineBaseComponent } from './booking-timeline.base-component';

type AlertType = 'success' | 'warning' | 'danger';

const UPDATE_ALERT_INTERVAL = 1000 * 10;
const UNDO_DROP_OFF_PERIOD = 2;

@Component({
  template: '',
  standalone: false,
})
export abstract class ManageBookingTimelineBaseComponent extends BaseComponent implements OnInit, OnDestroy {
  protected modalService = inject(ModalService);
  protected storageService = inject(SharedStorageService);
  protected featureResolver = inject(FeatureResolver);
  protected documentService = inject(SharedDocumentService);
  protected bookingService = inject(SharedBookingService);
  protected shopsService = inject(SharedShopsService);
  protected reviewsService = inject(SharedReviewsService);
  protected locationService = inject(SharedLocationService);
  protected googleMapsService = inject(SharedGoogleMapsService);
  protected appSettingsService = inject(SharedAppSettingsService);
  protected windowService = inject(SharedWindowService);
  protected route = inject(ActivatedRoute);
  protected translateService = inject(TranslateService);
  protected notify = inject(SharedNotificationService);
  protected analytics = inject(SharedAnalyticsService);
  protected resolver = inject(ComponentFactoryResolver);
  protected cd = inject(ChangeDetectorRef);
  protected location = inject(Location);
  protected router = inject(Router);

  /**
   * AM-4924 — set while the legacy drop-off sheet is being opened as the wizard's fallback, so that
   * opening it does not route straight back into the wizard.
   */
  private isDropoffWizardHandled = false;
  private isDropoffWizardOpen = false;

  @ViewChild('modalContent', { read: ViewContainerRef }) modalContent: ViewContainerRef;
  @ViewChild('modal') modal: ModalDirective;
  @ViewChild('bookingTimeline') bookingTimeline: BookingTimelineBaseComponent;
  @ViewChild('bookingOptions') bookingOptions: BookingOptionsBaseComponent;
  @Output() loading = new EventEmitter<boolean>(false);

  modalComponentRef: ComponentRef<BookingActionBaseComponent>;
  currentAction: BookingActionInfo;
  currentActionSubscriptions: Subscription[] = [];

  isCheckInSession = false;
  isSkipAction = false;

  shareBookingOptionsVisible = false;

  private _averageRating = 0;
  private _booking: Booking;
  private _shop: BookableStorageLocation;
  private _review: Review;
  private _strategy: BookingTimelineStrategy;

  private paramsSubscription: Subscription;

  private isDirty = false;
  private _isModalReady = false;
  private _isLoading = false;

  protected promptFeedbackTimer: NodeJS.Timeout;
  protected feedbackPrompted = false;
  protected promptExperiencesTimer: NodeJS.Timeout;
  protected experiencesPrompted = false;
  protected isUndoDropOffSkipped = false;
  protected isSelectPaymentMethodSkipped = false;

  protected alertType: AlertType;
  private _alertMessage: string;
  private _alertAction: BookingActionInfo;

  private previousScrollPosition = 0;

  private shareLink: string;

  constructor() {
    super();
    this.documentService.addBodyClass('manage-booking-timeline');
  }

  get isNative(): boolean {
    return this.windowService.isNative;
  }

  get isSharingAvailable(): boolean {
    return this.windowService.isSharingAvailable;
  }

  get isClipboardAvailable(): boolean {
    return this.windowService.isClipboardAvailable;
  }

  get isTabbedNavigationEnabled(): boolean {
    return this.windowService.isTouch && AppConfig.IS_TABBED_NAVIGATION_ENABLED;
  }

  get showOrderSummary(): boolean {
    if (!this.order) {
      return false;
    }
    return AppConfig.IS_ORDER_SUMMARY_ON_BOOKING_TIMELINE_ENABLED;
  }

  get showNumberOfReviews(): boolean {
    return this.numberOfReviews > 1;
  }

  get averageRating(): number {
    return this._averageRating;
  }
  set averageRating(value: number) {
    this._averageRating = value;
    this.cd.detectChanges();
  }

  get numberOfReviews(): number {
    if (!this.shop || !this.shop.stats) {
      return 0;
    }
    return this.shop.stats.numberOfRatings;
  }

  get storageLocationImage(): string {
    if (!this.shop || !this.shop.images || this.shop.images.length === 0) {
      return 'assets/no-image.jpg';
    }
    return `${Config.environment.IMAGES_BASE_URL}/images/medium/${this.shop.images[0]}`;
  }

  get inlineTimerMode(): boolean {
    if (!this.strategy) {
      return false;
    }
    return this.strategy.useInlineTimer;
  }

  get strategy(): BookingTimelineStrategy {
    return this._strategy;
  }
  set strategy(value: BookingTimelineStrategy) {
    this._strategy = value;

    // HACK: Fixes timer not showing up until something else triggers a UI update
    setTimeout(() => this.cd.markForCheck(), 0);
  }

  get alertMessage(): string {
    return this._alertMessage;
  }

  get alertAction(): BookingActionInfo {
    return this._alertAction;
  }

  get isSuccessAlert() {
    if (!this.alertType) {
      return false;
    }
    return this.alertType === 'success';
  }

  get isWarningAlert() {
    if (!this.alertType) {
      return false;
    }
    return this.alertType === 'warning';
  }

  get isDangerAlert() {
    if (!this.alertType) {
      return false;
    }
    return this.alertType === 'danger';
  }

  get modalId(): string {
    const actionName = this.currentAction ? this.currentAction.name : 'NoAction';
    return `actionModal_${actionName}`;
  }

  get modalTitle(): string {
    if (!this.currentAction) {
      return '';
    }
    if (this.isSkipAction && this.currentAction.skipTitle) {
      return this.currentAction.skipTitle;
    }
    return this.currentAction.title;
  }

  get isModalFullHeight(): boolean {
    if (this.currentAction?.viewSize === 'auto') {
      return false;
    }
    return this.modalTitle ? false : true;
  }

  get modalSubtitle(): string {
    if (!this.currentAction) {
      return '';
    }
    if (this.isSkipAction) {
      return this.currentAction.skipSubtitle;
    }
    return this.currentAction.subtitle;
  }

  get isModalReady(): boolean {
    return this._isModalReady;
  }
  set isModalReady(value: boolean) {
    this._isModalReady = value;
    this.cd.markForCheck();
  }

  get currency(): string {
    if (!this.booking) {
      return '';
    }
    return this.booking.price.final?.currency || this.booking.price.pricing.currency;
  }

  get booking(): Booking {
    return this._booking;
  }
  set booking(value: Booking) {
    this._booking = value;
    this.updateAlert();

    // FIXME: Remove these, should be updated via binding
    if (this.bookingTimeline) {
      this.bookingTimeline.booking = value;
    }
    if (this.bookingOptions) {
      this.bookingOptions.booking = value;
    }
  }

  get shop(): BookableStorageLocation {
    return this._shop;
  }
  set shop(value: BookableStorageLocation) {
    this._shop = value;
    this.cd.markForCheck();

    if (!value) {
      return;
    }
    this.averageRating = value.stats ? value.stats.averageRating : AppConfig.GLOBAL_AVERAGE_RATING;
  }

  get review(): Review {
    return this._review;
  }
  set review(value: Review) {
    this._review = value;
    this.cd.markForCheck();
  }

  get isLoading(): boolean {
    return this._isLoading;
  }
  set isLoading(value: boolean) {
    this._isLoading = value;
    this.loading.emit(value);
    this.cd.markForCheck();
  }

  get ios(): boolean {
    return this.windowService.iOS;
  }

  get modalError(): boolean {
    return this.modalTitle && this.currentAction && this.currentAction.error;
  }

  get modalWarning(): boolean {
    return this.modalTitle && this.currentAction && this.currentAction.warning;
  }

  get modalSuccess(): boolean {
    return this.modalTitle && this.currentAction && this.currentAction.success;
  }

  get order(): LegacyOrder {
    return this.booking?.orderDetails?.length > 0
      ? this.booking.orderDetails[this.booking.orderDetails.length - 1]
      : null;
  }

  get isSettled(): boolean {
    return ['PAID', 'CANCELLED'].includes(this.booking?.status);
  }

  get orderSummaryHeadline(): string {
    return this.booking?.status === 'CANCELLED' ? 'BOOKING_CANCELED' : 'BOOKING_SUMMARY';
  }

  get finalPrice(): IPrice {
    return this.booking.price.final;
  }

  get isUndoDropOffAllowed(): boolean {
    if (!AppConfig.IS_UNDO_DROPOFF_ENABLED) {
      return false;
    }
    if (this.booking.status !== 'CHECKED_IN') {
      return false;
    }
    const minutesPassed = DateUtil.minutesSinceCheckIn(this.booking);
    return minutesPassed <= UNDO_DROP_OFF_PERIOD;
  }

  get isDirectionsVisible(): boolean {
    if (
      !this.booking.paymentMethodId &&
      this.bookingTimeline &&
      this.bookingTimeline.config.paymentInfoRequiredBeforeDirections
    ) {
      return false;
    }
    return true;
  }

  get isAccessCodeBasedSecurity(): boolean {
    return this.shop?.isAccessCodeBasedSecurity || false;
  }

  get isSelfService(): boolean {
    return this.shop?.isSelfService ? true : false;
  }

  get showAccessInfo(): Observable<boolean> {
    if (!this.isSelfService) {
      return observableOf(false);
    }
    return this.appSettingsService.$current.pipe(
      map((settings) => settings?.bookingStatusesWithAccessInfoEnabled.includes(this.booking?.status)),
    );
  }

  protected get addLuggageImageAction(): BookingActionInfo {
    return new BookingActionInfo('PromptLuggageImageFromBookingTimeline')
      .withAction({ componentToShow: this.addLuggageImageComponentType })
      .withTitle(this.translate('TAKE_A_PHOTO_OF_YOUR_LUGGAGE'));
  }

  protected get validateBookingActionInfo(): BookingActionInfo {
    return new BookingActionInfo('ViewValidateBookingFromBookingTimeline').withAction({
      componentToShow: this.validateBookingActionComponentType,
    });
  }

  protected get dropOffActionInfo(): BookingActionInfo {
    return new BookingActionInfo('StartStorageTimerFromBookingTimeline').withAction({
      componentToShow: this.dropOffActionComponentType,
    });
  }

  protected get viewStorageLocationActionInfo(): BookingActionInfo {
    if (!this.booking.paymentMethodId && this.bookingTimeline.config.paymentInfoRequiredBeforeDirections) {
      return new BookingActionInfo('ViewStorageLocationFromBookingTimeline')
        .withCallToAction(this.translate('GET_DIRECTIONS'))
        .withAction({
          functionToCall: () => {
            const message = this.translate('PAYMENT_METHOD_BEFORE_DIRECTIONS_ALERT');
            const title = this.translate('PAYMENT_METHOD_MISSING');
            this.windowService.alert(message, title);

            return Promise.resolve(null);
          },
          componentToShow: this.selectPaymentMethodActionComponentType,
        })
        .withTitle(this.translate('DIRECTIONS_AFTER_PAYMENT_METHOD'))
        .withSubtitle(this.translate('PAYMENT_METHOD_MISSING'))
        .withBookingChangeHandler((b) => this.onPaymentMethodChange(b));
    }
    return new BookingActionInfo('ViewStorageLocationFromBookingTimeline')
      .withAction({
        componentToShow: this.goToShopActionComponentType,
      })
      .withViewSize('auto');
  }

  protected get leaveFeedbackActionInfo(): BookingActionInfo {
    return (
      new BookingActionInfo('PromptFeedbackFromBookingTimeline')
        .withAction({ componentToShow: this.leaveFeedbackActionComponentType })
        .withTitle(this.translate('FEEDBACK'))
        // AM-4364 — fullscreen + chrome-less like the other review surfaces; the no-rating
        // state owns its escape hatch via the docked dismiss link in lh-review.
        .withFullscreenReview(true)
        .withReviewChangeHandler((r) => void this.onFeedbackReceived(r))
    );
  }

  protected get exploreExperiencesActionInfo(): BookingActionInfo {
    return new BookingActionInfo('PromptExperiencesFromBookingTimeline')
      .withAction({ componentToShow: this.exploreExperiencesActionComponentType })
      .withTitle(this.translate('YOU_ARE_FREE'))
      .withSubtitle(this.translate('GO_ENJOY_THE_CITY_WE_GOT_YOUR_STUFF'));
  }

  protected get explainStorageTimerActionInfo(): BookingActionInfo {
    return new BookingActionInfo('ExplainStorageTimerFromBookingTimeline')
      .withAction({ componentToShow: this.explainStorageTimerActionComponentType })
      .withTitle(this.translate('THE_STORAGE_TIMER'))
      .withSubtitle(this.translate('KEEPS_TRACK_OF_HOW_LONG_YOU_STORE'));
  }

  protected get selectPaymentMethodActionInfo(): BookingActionInfo {
    return new BookingActionInfo('SelectPaymentMethodFromMissingPaymentMethodAlert')
      .withCallToAction(this.translate('ADD_NOW'))
      .withAction({ componentToShow: this.selectPaymentMethodActionComponentType })
      .withSkipAction({
        functionToCall: () => {
          this.isSelectPaymentMethodSkipped = true;
          return observableOf<BookingChange>(null);
        },
      })
      .withTitle(this.translate('PAYMENT_OPTIONS'));
  }

  protected get undoDropOffActionInfo(): BookingActionInfo {
    return new BookingActionInfo('UndoDropOffFromDropOffSuccessAlert')
      .withCallToAction(this.translate('UNDO'))
      .withAction({ componentToShow: this.undoDropOffActionComponentType })
      .withSkipAction({
        functionToCall: () => {
          this.isUndoDropOffSkipped = true;
          return observableOf<BookingChange>(null);
        },
      })
      .withTitle(this.translate('DID_YOU_START_STORAGE_TIMER_BY_MISTAKE'));
  }

  protected get reportIncidentActionInfo(): BookingActionInfo {
    return new BookingActionInfo('ReportIncidentFromBookingTimeline')
      .withCallToAction(this.translate('REPORT_INCIDENT'))
      .withAction({ componentToShow: this.reportIncidentActionComponentType })
      .withTitle(this.translate('REPORT_INCIDENT'));
  }

  protected get orderSummaryActionInfo(): BookingActionInfo {
    const isPaid =
      ['CHECKED_OUT', 'PAID'].includes(this.booking.status) ||
      (this.booking.status === 'CANCELLED' && (this.order?.total || this.booking.price.final?.total > 0))
        ? true
        : false;

    if (isPaid) {
      let titleKey: string;
      let subtitleKey: string;
      let state: BookingActionState;

      const isPaymentFailed = this.booking.status === 'CHECKED_OUT' && !this.booking.paidDirectly;
      if (isPaymentFailed) {
        titleKey = 'PAYMENT_FAILED';
        state = 'error';
      } else if (this.booking.paidDirectly) {
        titleKey = 'PAY_IN_SHOP';
        subtitleKey = 'SHOW_THE_RECEIPT_AND_PAY_IN_SHOP';
        state = 'warning';
      } else {
        titleKey = 'PAYMENT_SUCCEEDED';
        subtitleKey =
          this.booking.status !== 'CANCELLED'
            ? afterPaymentInstructionKey({ isSelfService: this.isSelfService })
            : null;
        state = 'success';
      }

      return new BookingActionInfo('ShowReceiptActionFromBookingOptions')
        .withCallToAction(this.translate('RECEIPT'))
        .withAction({ componentToShow: this.showReceiptActionComponentType })
        .withTitle(this.translate(titleKey))
        .withSubtitle(subtitleKey && this.translate(subtitleKey))
        .withState(state);
    }

    if (this.booking.status === 'CANCELLED') {
      return null;
    }

    return new BookingActionInfo('ModifyBookingFromInlineBookingSummary')
      .withCallToAction(this.translate('MODIFY_BOOKING_SHORT'))
      .withAction({ componentToShow: this.modifyBookingActionComponentType })
      .withTitle(this.translate('MODIFY_BOOKING_CTA'));
  }

  protected abstract get explainStorageTimerActionComponentType(): Type<ExplainStorageTimerActionBaseComponent>;
  protected abstract get leaveFeedbackActionComponentType(): Type<LeaveFeedbackActionBaseComponent>;
  protected abstract get exploreExperiencesActionComponentType(): Type<ExploreExperiencesActionBaseComponent>;
  protected abstract get selectPaymentMethodActionComponentType(): Type<SelectPaymentMethodActionBaseComponent>;
  protected abstract get undoDropOffActionComponentType(): Type<UndoDropOffActionBaseComponent>;
  protected abstract get modifyBookingActionComponentType(): Type<ModifyBookingActionBaseComponent>;
  protected abstract get showReceiptActionComponentType(): Type<ShowReceiptActionBaseComponent>;
  protected abstract get goToShopActionComponentType(): Type<GoToShopActionBaseComponent>;
  protected abstract get addLuggageImageComponentType(): Type<DropOffActionBaseComponent>;
  protected abstract get validateBookingActionComponentType(): Type<ValidateBookingActionBaseComponent>;
  protected abstract get dropOffActionComponentType(): Type<DropOffActionBaseComponent>;
  protected abstract get reportIncidentActionComponentType(): Type<ReportIncidentActionBaseComponent>;

  ngOnInit() {
    this.averageRating = this.shop && this.shop.stats ? this.shop.stats.averageRating : AppConfig.GLOBAL_AVERAGE_RATING;

    // This is to make sure the shop criteria are available
    void this.locationService.init().then(() => this.cd.markForCheck());
    this.paramsSubscription = this.route.params.subscribe(
      (params: { id: string; luggagePhotoRequired: string; linkToPlaces: string }) => {
        if (params.id) {
          if (Config.isDevelopment) {
            console.log(`Found booking id in params`, {
              params,
              luggagePhotoRequired: params.luggagePhotoRequired,
              typeOfLuggagePhotoRequired: typeof params.luggagePhotoRequired,
            });
          }
          const isLuggagePhotoRequired = params.luggagePhotoRequired === 'true';
          void this.loadBooking(params.id, isLuggagePhotoRequired)
            .then(() => this.openDropoffWizardFromRoute())
            .catch(() => undefined);

          if (params.linkToPlaces) {
            this.googleMapsService.linkToPlaces = params.linkToPlaces === 'true';
          }
        } else {
          this.cd.markForCheck();
        }
      },
    );

    this.scheduleUpdateAlert(true);
  }

  ngOnDestroy() {
    this.documentService.removeBodyClass('manage-booking-timeline');
    this.destroyCurrentAction();
    try {
      this.paramsSubscription.unsubscribe();
    } catch {
      // Ignore
    }
  }

  async onAction(actionInfo: BookingActionInfo, skip = false, delay?: number) {
    if (delay > 0) {
      // Trigger action after the specified delay
      setTimeout(() => void this.onAction(actionInfo, skip), delay);
      return;
    }
    // AM-4924 — drop-off on a confirmed pre-booking goes through the drop-off wizard instead of the
    // legacy sheet, while the wizard is on. Every way the timeline opens drop-off arrives here, so this
    // is the one place it is intercepted.
    const isDropOff = !skip && actionInfo?.action?.componentToShow === this.dropOffActionComponentType;
    if (isDropOff && !this.isDropoffWizardHandled && this.isDropoffWizardUsed()) {
      await this.openDropoffWizard(actionInfo, 'timeline');
      return;
    }
    // Remove previous modal content and subscriptions
    this.destroyCurrentAction();

    // Set the currently selected action
    this.currentAction = actionInfo;
    this.isSkipAction = skip;

    // Determine which action to take
    const actionToTake = skip ? actionInfo.skipAction : actionInfo.action;

    // Show component and/or call function defined by the action
    await this.callActionFunction(actionToTake.functionToCall);
    this.showActionModal(actionToTake.componentToShow);
  }

  onAlertClicked() {
    if (!this.alertAction) {
      return;
    }
    void this.onAction(this.alertAction);
  }

  dismissAlert() {
    void this.onAction(this.alertAction, true);
    this.setAlert(null, null, null);
  }

  explainStorageTimer() {
    void this.onAction(this.explainStorageTimerActionInfo);
  }

  viewStorageAccess() {
    if (this.booking.status === 'CONFIRMED') {
      void this.onAction(this.dropOffActionInfo);
    } else {
      void this.onAction(this.validateBookingActionInfo);
    }
  }

  viewStorageLocation() {
    void this.onAction(this.viewStorageLocationActionInfo);
  }

  toggleShareBookingOptions() {
    this.shareBookingOptionsVisible = !this.shareBookingOptionsVisible;
  }

  async getLinkForSharing(): Promise<string> {
    // TODO: Store in local or session storage
    if (!this.shareLink) {
      this.shareLink = await this.bookingService.getShareableLink(this.booking._id);
    }
    return this.shareLink;
  }

  async shareBookingCopyToClipboard() {
    try {
      // Get shareable link for the booking and copy to the clipboard
      await this.windowService.writeToClipboard(this.shareLink || this.getLinkForSharing());

      // Notify the user that the link was copied
      this.notify.success(this.translate('LINK_COPIED'));
    } catch (err) {
      // Notify the user that something went wrong
      this.notify.warning(`Error copying link: ${(err as { message?: string }).message}`);
    }
  }

  async shareBookingVia() {
    if (!this.isSharingAvailable) {
      return;
    }

    try {
      const url = await this.getLinkForSharing();
      const text = `${this.translate('LUGGAGEHERO_BOOKING_AT_LOCATION')} ${this.shop.name}`;

      await this.windowService.shareLink(url, text);
    } catch {
      this.notify.warning(`Sharing failed, please check your connection and try again`);
    }
  }

  /**
   * AM-4924 — the app layer's sheet hosting the drop-off wizard. `null` (the default) means this surface
   * has none, and drop-off opens the legacy sheet as it always has.
   */
  protected get dropoffWizardModalComponentType(): Type<DropoffWizardModalHost> | null {
    return null;
  }

  /** Extra presentation for the wizard's sheet, e.g. Ionic sheet breakpoints. */
  protected get dropoffWizardModalOptions(): Partial<ModalOptions<DropoffWizardModalHost>> {
    return {};
  }

  /**
   * Whether drop-off goes through the wizard: the surface has a sheet for it, the wizard is on for this
   * client, and the booking is a confirmed pre-booking. Walk-in, guest and partner bookings keep the
   * legacy sheet. Whether to offer cover inside it, the wizard decides.
   */
  protected isDropoffWizardUsed(): boolean {
    if (!this.dropoffWizardModalComponentType || !this.booking) {
      return false;
    }
    if (!this.featureResolver.isDropoffWizard) {
      return false;
    }
    return dropoffWizardEligibility(this.booking).kind === 'eligible';
  }

  /**
   * `/bookings/:id/dropoff` opened directly (a link, a reload).
   *
   * Where the wizard applies, the router is first moved to `/bookings/:id` — replacing the link's entry —
   * carrying a one-shot `dropoffWizard` state, and the timeline that loads there opens the wizard the
   * same way a tap does. That gives a link the same history as a tap, `/bookings/:id` under
   * `/bookings/:id/dropoff`, so Back from either lands on the booking (the ticket's acceptance), and it
   * keeps the router on `/bookings/:id` while the sheet is up, so its own Back is a same-URL no-op for
   * the router rather than a reload of the page under the sheet.
   *
   * Where it does not apply, the address goes back to the booking and nothing else happens.
   */
  private openDropoffWizardFromRoute(): void {
    if (!this.booking) {
      // The booking did not load: leave no address behind that names a wizard that never opened.
      const id = this.route.snapshot.params?.['id'] as string | undefined;
      if (this.route.snapshot.data?.['dropoffWizard'] && id) {
        this.location.replaceState(`/bookings/${id}`);
      }
      return;
    }
    const bookingId = this.booking._id;
    const state = (this.location.getState() ?? {}) as { dropoffWizard?: boolean };
    if (state.dropoffWizard) {
      // Spend the one-shot state, so a reload or a later Back to this entry does not reopen the wizard.
      this.location.replaceState(this.location.path(), '', { ...state, dropoffWizard: false });
      if (this.isDropoffWizardUsed()) {
        void this.openDropoffWizard(this.dropOffActionInfo, 'link');
      }
      return;
    }
    if (!this.route.snapshot.data?.['dropoffWizard']) {
      return;
    }
    if (!this.isDropoffWizardUsed()) {
      this.location.replaceState(`/bookings/${bookingId}`);
      return;
    }
    void this.router.navigate(['/bookings', bookingId], { replaceUrl: true, state: { dropoffWizard: true } });
  }

  /**
   * Show the drop-off wizard over the timeline, at its own address, and act on how it ended.
   *
   * The wizard's address is **pushed** over `/bookings/:id` (the ticket: "Back/close returns to the
   * timeline at /bookings/{id}"):
   * - closing, or finishing, steps back off it to `/bookings/:id`;
   * - the browser's (or Android's) Back while the sheet is up lands on `/bookings/:id` and closes the
   *   sheet as a close.
   * The push goes through `Location`, not the router, so the timeline under the sheet is never rebuilt.
   */
  private async openDropoffWizard(dropOffAction: BookingActionInfo, entryPoint: 'timeline' | 'link'): Promise<void> {
    const component = this.dropoffWizardModalComponentType;
    if (!component || this.isDropoffWizardOpen) {
      return;
    }
    this.isDropoffWizardOpen = true;
    const bookingId = this.booking._id;
    const wizardPath = `/bookings/${bookingId}/dropoff`;
    let outcome: DropoffWizardOutcome = { kind: 'closed' };
    let leftByBack = false;
    this.location.go(wizardPath);

    let ref: ModalRef;
    try {
      ref = await this.modalService.show({
        ...this.dropoffWizardModalOptions,
        component,
        componentState: { bookingId, entryPoint, onFinished: (finished: DropoffWizardOutcome) => (outcome = finished) },
      });
    } catch {
      // The sheet could not open: put the address back and fall back to the legacy drop-off, rather than
      // leaving the wizard marked open — which would block drop-off for the session.
      this.isDropoffWizardOpen = false;
      this.location.back();
      this.openLegacyDropOff(dropOffAction, 0);
      return;
    }

    // Back while the sheet is up: the address has already left the wizard, so close the sheet to match.
    const popstate = this.location.subscribe((event) => {
      if (!event.url?.endsWith('/dropoff')) {
        leftByBack = true;
        void ref.hide();
      }
    });

    // Ionic reports a dismissal through `result`, the web through `onHidden`; either ends the wizard once.
    let isSettled = false;
    const settle = () => {
      if (isSettled) {
        return;
      }
      isSettled = true;
      this.isDropoffWizardOpen = false;
      popstate.unsubscribe();
      if (!leftByBack && this.location.path().endsWith('/dropoff')) {
        this.location.back();
      }
      // Back after the drop-off still reports it: the booking is checked in either way.
      const ended = leftByBack && outcome.kind !== 'checked-in' ? { kind: 'closed' as const } : outcome;
      void this.onDropoffWizardFinished(ended, dropOffAction, bookingId);
    };
    ref.result?.subscribe(settle);
    ref.onHidden?.subscribe(settle);
  }

  /**
   * After the wizard, the timeline holds the booking **as the server has it**:
   *
   * - `checked-in` carries it, and then the existing drop-off confirmation opens, exactly as the legacy
   *   drop-off sheet opens it after checking in — staff at the locations are instructed to look for that
   *   screen;
   * - `closed` reloads it — a photo may have been attached, and a sheet dismissed mid-write means only the
   *   server knows what landed;
   * - `skipped` hands over to the legacy drop-off sheet.
   */
  private async onDropoffWizardFinished(
    outcome: DropoffWizardOutcome,
    dropOffAction: BookingActionInfo,
    bookingId: string,
  ): Promise<void> {
    if (outcome.kind === 'checked-in') {
      this.onBookingChange(outcome.booking);
      if (AppConfig.IS_DROPOFF_CONFIRMATION_ACTION_ENABLED) {
        // The delay is the one the timeline uses between two modals (see `showActionModal`).
        setTimeout(() => void this.onAction(this.validateBookingActionInfo), 600);
      }
      return;
    }
    try {
      this.onBookingChange(await this.bookingService.getBooking(bookingId, true));
    } catch {
      // Keep the booking we have; a failed read must not block the traveler at the counter.
    }
    if (outcome.kind === 'skipped') {
      this.openLegacyDropOff(dropOffAction, 600);
    }
  }

  private openLegacyDropOff(dropOffAction: BookingActionInfo, delay: number): void {
    setTimeout(() => {
      this.isDropoffWizardHandled = true;
      void this.onAction(dropOffAction).finally(() => (this.isDropoffWizardHandled = false));
    }, delay);
  }

  protected showActionModal(actionComponentType: Type<BookingActionBaseComponent>) {
    // Check if there is a component to show
    if (!actionComponentType) {
      return;
    }
    if (actionComponentType === this.leaveFeedbackActionComponentType) {
      this.feedbackPrompted = true;
    } else {
      this.cancelPromptFeedback();
    }
    if (actionComponentType === this.exploreExperiencesActionComponentType) {
      this.experiencesPrompted = true;
    } else {
      this.cancelPromptExperiences();
    }
    // Dynamically create the relevant action component inside the modal
    this.modalComponentRef = this.modalContent.createComponent(actionComponentType);

    // AM-4364 Variant B — propagate whether this action was launched from the green
    // validate-booking success surface, so the leave-feedback modal can apply its
    // green→white wash + fullscreen review only on that path (not, e.g., the chrome'd
    // "edit review" path opened from the timeline).
    this.modalComponentRef.instance.enterFromSuccess = this.currentAction?.enterFromSuccess ?? false;
    // AM-4364 — always-rated review entry points (rating strip + edit review) render
    // fullscreen and own their header, so the shared modal chrome is hidden (see template).
    this.modalComponentRef.instance.fullscreen = this.currentAction?.fullscreenReview ?? false;

    // Subscribe to booking and review changes from the action component
    this.addActionSubscription(this.modalComponentRef.instance.bookingChange, (b) => this.onBookingChange(b));
    this.addActionSubscription(this.modalComponentRef.instance.reviewChange, (r) => this.onReviewChange(r));

    // Subscribe to action requests from the action component
    this.addActionSubscription(
      this.modalComponentRef.instance.actionRequest,
      (requestedAction: BookingActionInfo) =>
        // HACK: the default 600ms delay prevents a page-scroll issue after the action modal
        // closes. In-modal swaps that don't close the modal (e.g. AM-4364 post-validation
        // rating → review) opt into a shorter delay via `withTransitionDelay`.
        void this.onAction(requestedAction, false, requestedAction.transitionDelayMs ?? 600),
    );

    // Hide the modal when the action component is requested closed
    this.addActionSubscription(this.modalComponentRef.instance.closed, () => this.hideActionModal());

    // Listen to when the modal is shown and hidden
    this.addActionSubscription(this.modal.onShow, () => this.onModalShow());
    this.addActionSubscription(this.modal.onHide, () => this.onModalHide());
    this.addActionSubscription(this.modal.onHidden, () => this.onModalHidden());

    this.isModalReady = true;

    if (!this.modal.isShown) {
      // Show the modal with the action component
      this.modal.show();
    }
  }

  protected onModalShow() {
    this.modalService.incrementCount();
    this.previousScrollPosition = this.windowService.scrollY;

    if (AppConfig.IS_BOOKING_ACTION_MODAL_AUTO_SCROLL_ENABLED) {
      this.windowService.scrollToTop();
    }
  }

  protected onModalHide() {
    this.modalService.decrementCount();

    if (this.previousScrollPosition > 0 && AppConfig.IS_BOOKING_ACTION_MODAL_AUTO_SCROLL_ENABLED) {
      // Scroll back down to where we were on the page before the modal was shown
      this.windowService.scrollVertical(this.previousScrollPosition);
    }
  }

  protected onModalHidden() {
    this.isModalReady = false;
    if (this.modalComponentRef && this.modalComponentRef.instance) {
      this.modalComponentRef.instance.onHidden();
    }
    this.promptFeedback(1000);
    this.promptExperiences(1500);
  }

  protected hideActionModal() {
    if (this.isDirty) {
      this.windowService.scrollToBottom();
      this.previousScrollPosition = this.windowService.scrollX;
    }
    if (this.modal?.isShown) {
      this.modal.hide();
    }
  }

  protected promptLuggageImage() {
    if (this.shop.noSecuritySeals) {
      void this.onAction(this.addLuggageImageAction);
    }
  }

  protected promptExperiences(delay: number) {
    if (this.booking.status !== 'CHECKED_IN' || this.booking.state.exploreExperiencesDone || this.experiencesPrompted) {
      return false;
    }
    if (!AppConfig.CITIES_WITH_EXPERIENCES_AUTO_PROMPTED.includes(this.shop.officialLocationKey)) {
      return false;
    }
    this.promptExperiencesTimer = setTimeout(() => void this.onAction(this.exploreExperiencesActionInfo), delay);
  }

  protected cancelPromptExperiences() {
    if (!this.promptExperiencesTimer) {
      return;
    }
    clearTimeout(this.promptExperiencesTimer);
    this.promptExperiencesTimer = null;
  }

  protected promptFeedback(delay: number) {
    // AM-4364 — never prompt no-show bookings: the traveler didn't use the service (often
    // charged without showing up), so a review/rating ask is inappropriate and skews negative.
    if (
      this.booking.status !== 'PAID' ||
      this.booking.feedbackReceived ||
      this.feedbackPrompted ||
      this.booking.chargedNoShow
    ) {
      return;
    }
    // AM-4364 — when either pickup-review flow is active, that flow's own surface owns
    // review collection (Variant A: multi-step pickup modal; Variant B: rating strip on
    // the green validate-booking view). Suppress the post-pickup leave-feedback prompt in
    // both cases. The effective flow accounts for the `pickupReviewFlow` feature flag as
    // well as the temporary experiment variant. The "Leave feedback" CTA on the booking
    // timeline still gives the user a path to edit the review later.
    const flow = this.featureResolver.pickupReviewFlow;
    if (flow === 'in-pickup-modal' || flow === 'post-validation') {
      return;
    }
    this.promptFeedbackTimer = setTimeout(() => void this.onAction(this.leaveFeedbackActionInfo), delay);
  }

  protected cancelPromptFeedback() {
    if (!this.promptFeedbackTimer) {
      return;
    }
    clearTimeout(this.promptFeedbackTimer);
    this.promptFeedbackTimer = null;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async onFeedbackReceived(review: Review) {
    const booking = await this.bookingService.getBooking(this.booking._id, true);
    this.onBookingChange(booking);
  }

  protected scheduleUpdateAlert(continuous = false) {
    setTimeout(() => {
      this.updateAlert();

      if (continuous) {
        this.scheduleUpdateAlert(true);
      }
    }, UPDATE_ALERT_INTERVAL);
  }

  protected updateAlert() {
    if (!this.booking) {
      return;
    }
    if (!this.booking.paymentMethodId && !this.isSelectPaymentMethodSkipped) {
      this.setAlert('danger', this.translate('PAYMENT_METHOD_MISSING'), this.selectPaymentMethodActionInfo);
      return;
    }
    if (this.isUndoDropOffAllowed && !this.isUndoDropOffSkipped) {
      const bagCount = this.booking.luggage.normal + this.booking.luggage.hand;
      const bagsText = this.translate(bagCount === 1 ? 'BAG' : 'BAGS').toLowerCase();
      const youDroppedOff = this.translate('YOU_DROPPED_OFF_X_BAGS');
      this.setAlert('success', `${youDroppedOff} ${bagCount} ${bagsText}`, this.undoDropOffActionInfo);
      return;
    }
    this.setAlert(null, null, null);
  }

  protected setAlert(type: AlertType, message: string, action?: BookingActionInfo) {
    this.alertType = type;
    this._alertMessage = message;
    this._alertAction = action;
    this.cd.markForCheck();
  }

  protected onBookingChange(value: Booking) {
    if (value.status === 'CHECKED_IN' && this.booking.status !== 'CHECKED_IN') {
      this.isCheckInSession = true;
    }
    // TODO: Check if anything actually changed
    this.booking = value;
    this.isDirty = true;

    // Notify booking change handler on current action if present
    if (this.currentAction && this.currentAction.bookingChangeHandler) {
      this.currentAction.bookingChangeHandler(value);
    }
  }

  protected onReviewChange(value: Review) {
    // TODO: Check if anything actually changed
    this.review = value;
    this.isDirty = true;

    // Notify booking change handler on current action if present
    if (this.currentAction && this.currentAction.reviewChangeHandler) {
      this.currentAction.reviewChangeHandler(value);
    }
  }

  protected async callActionFunction(
    actionFunction: () => Observable<BookingChange> | Promise<BookingChange>,
  ): Promise<void> {
    // Check if there is an function to call
    if (!actionFunction) {
      return;
    }

    // Call the function to perform the action
    const asyncAction = actionFunction();

    // Wait for the result to record any changes returned
    const change = await (isObservable(asyncAction) ? asyncAction.toPromise() : asyncAction);

    if (!change) {
      return;
    }
    switch (change.changeType) {
      case 'booking':
        this.booking = change.changedValue as Booking;
        break;

      case 'review':
        this.review = change.changedValue as Review;
        break;
    }
  }

  private onPaymentMethodChange(booking: Booking) {
    if (!booking.paymentMethodId) {
      return;
    }
    this.viewStorageLocation();
  }

  protected destroyCurrentAction() {
    this.hideActionModal();

    try {
      if (this.currentActionSubscriptions.length > 0) {
        this.currentActionSubscriptions.forEach((subscription) => subscription.unsubscribe());
        this.currentActionSubscriptions = [];
      }
    } catch {
      // Ignore
    }

    if (this.modalContent) {
      this.modalContent.clear();
    }
    if (this.modalComponentRef) {
      this.modalComponentRef.destroy();
      this.modalComponentRef = null;
    }
  }

  protected addActionSubscription<T>(observable: Observable<T>, handler: (value: T) => void) {
    this.currentActionSubscriptions.push(observable.subscribe(handler));
  }

  // Refresh mainly used by the user active component as call back
  public refreshBooking(refresh: boolean) {
    if (refresh && this.booking) {
      void this.loadBooking(this.booking._id, false, refresh);
    }
  }

  private async loadBooking(id: string, promptLuggageImage = false, refresh = false): Promise<void> {
    this.isLoading = true;

    try {
      console.log(`Loading booking with ID: ${id} (refresh: ${refresh})`);
      const booking = await this.bookingService.getBooking(id, refresh);

      console.log(`Loaded booking with ID: ${id}`, booking);

      // Record user ID for analytics purposes
      this.analytics.identify({ userId: booking.userId });

      try {
        // Fetch shop details
        this.shop = await this.shopsService.getShopDetails(
          booking.shopId,
          DateUtil.serializeDate(booking.period.from),
          DateUtil.serializeDate(booking.period.to),
          booking.luggage.normal,
          booking.luggage.hand,
          booking.isGuest || booking.isWalkIn ? 'dropoff' : 'marketplace',
        );
      } catch (err) {
        // TODO: Handle shops that are deleted or no longer active
        console.log(`Error loading shop details`, err);
      }

      this.review = await this.reviewsService.getReview(booking._id);
      this.booking = booking;

      const isLuggageImageAdded = this.booking.luggage.smartImages?.length > 0;
      if (promptLuggageImage && !isLuggageImageAdded) {
        this.promptLuggageImage();
      } else {
        this.promptFeedback(3000);
      }
    } catch {
      // TODO: Handle errors
    }

    this.isLoading = false;
  }

  private translate(key: string): string {
    return this.translateService.instant(key) as string;
  }
}

/**
 * AM-4924 — what the app layer's drop-off wizard sheet receives. The sheet mounts `lh-dropoff-wizard`
 * with `bookingId` and `entryPoint`, calls `onFinished` with the wizard's outcome and then hides itself.
 */
export interface DropoffWizardModalState {
  bookingId: string;
  entryPoint: 'timeline' | 'link';
  onFinished: (outcome: DropoffWizardOutcome) => void;
}

export type DropoffWizardModalHost = BaseModalComponent & DropoffWizardModalState;
